import { useCreateNewOrganization } from 'api/InternalAdminAPI';
import { viewAsUserRequest } from 'core/userContext';
import { createUserIdConfig } from 'modules/projects/utils';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ShrinkingButton } from 'ui/button';
import { StickyPageHeader } from 'ui/pageHeader';
import { SideNav } from 'ui/sideNav';
import { useScrollIntoView } from 'utils/hooks/useScrollIntoView';
import { NewOrganizationFormProvider, NewOrganizationFormSchemaType } from '../contexts';
import { AdministratorFormSection } from './AdministratorFormSection';
import { sideNavLinksAndSections } from './NewOrganizationPage.constants';
import {
  ContentWrapper,
  FlexContainer,
  H3NoMargin,
  InlineFlex,
  Layout,
  PageWrapper,
} from './NewOrganizationPage.styled';
import { OrganizationFormSection } from './OrganizationFormSection';
import { ProjectFormSection } from './ProjectFormSection';

export const TEXT = {
  HEADER: 'New Organization',
  CREATE_BUTTON: 'Create organization',
};

export const NewOrganizationModernized = () => {
  useScrollIntoView();
  const dispatch = useDispatch();
  const {
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useFormContext<NewOrganizationFormSchemaType>();

  const [hasTrialEndDate, setHasTrialEndDate] = useState(false);
  const toggleHasTrialEndDate = () => setHasTrialEndDate(!hasTrialEndDate);

  const { mutate, isLoading } = useCreateNewOrganization({
    onSuccess: (response) => {
      const msg =
        typeof response === 'string' ? response : 'Successfully created a new organization.';
      dispatch(addPositiveToast(msg));
      const email = getValues('email');
      dispatch(viewAsUserRequest(email));
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || '';
      dispatch(
        addNegativeToast(`There was an error creating a new organization. ${errorMessage}.`),
      );
    },
  });

  const onSubmit = (values: NewOrganizationFormSchemaType) => {
    const { uniqueIdentifier, trialEndDate, ...rest } = values;
    const { isUuidBased, uuidProjectType } = createUserIdConfig(uniqueIdentifier);
    const payload = {
      ...rest,
      isUuidBased,
      uuidProjectType,
      ...(hasTrialEndDate
        ? { trialEndDate: trialEndDate?.format('yyyy-MM-DD') }
        : { trialEndDate: null }),
    };

    mutate(payload);
  };

  return (
    <PageWrapper>
      <ContentWrapper>
        <StickyPageHeader>
          <FlexContainer>
            <InlineFlex>
              <H3NoMargin>{TEXT.HEADER}</H3NoMargin>
            </InlineFlex>
            {/* Opted not to use PageActions for this PrimaryButton, because the PageActions
              has a top padding that we would have to override.
            */}
            <ShrinkingButton
              isDisabled={!isValid || isLoading}
              isLoading={isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              {TEXT.CREATE_BUTTON}
            </ShrinkingButton>
          </FlexContainer>
        </StickyPageHeader>
        <Layout>
          <SideNav isSticky stickyPadding={80} linksAndSections={sideNavLinksAndSections} />
          <div>
            <OrganizationFormSection
              hasTrialEndDate={hasTrialEndDate}
              toggleHasTrialEndDate={toggleHasTrialEndDate}
            />
            <AdministratorFormSection />
            <ProjectFormSection />
          </div>
        </Layout>
      </ContentWrapper>
    </PageWrapper>
  );
};

export const NewOrganizationPage = () => (
  <NewOrganizationFormProvider>
    <NewOrganizationModernized />
  </NewOrganizationFormProvider>
);
